import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { ConfigProvider } from 'tea-component';
import moment from "moment";
import DateTypeSegment from "@/pages/datalab/components/DateComponents/DateTypeSegment";
import { RangePicker } from '@home/components/RangePicker/RangePicker';
import { getLastDateRange } from "@/pages/datalab/utils/utils";
import classNames from "classnames";
import './index.scss';
// import {adjustOverlayPosition} from "@/pages/datalab/utils/overlayPositionAdjuster";
import { START_OF_DATE } from '@/pages/dashboard/utils/_config/constant';
import { DATALAB_DATE_EVENT } from '@dbl/core/global';
import { getLocaleState } from '@home/utils/state';
import DateEventShortcut from '../DateEventShortcut';
import { getDatalabShortcut } from '@/pages/datalab/components/DateComponents/utils';
import { getPreviousPeriodValue } from '@dbl/core/date';
import BaseTimeAndCompareRange from "@/pages/datalab/components/DateComponents/BaseTimeAndCompareRange";
import CompareSwitch from "@/pages/datalab/components/DateComponents/CompareSwitch";
import {adjustOverlayPositionByPlacement} from "@/pages/datalab/utils/overlayPositionAdjuster";
import type { TDatalabDateEvent } from '@dbl/core/global';

/**
 * 静态时间范围选择
 * @param props
 * @constructor
 */
function StaticDate(props: any) {
  const {isEn, value = {dateRange: [], dateType: 'day'}, onChange, children, className, close, isDisabledCompare, getOverlayPositionConfig, ...reset} = props;
  const rangePickerRef = useRef(null);
  const isConfirmRef = useRef<boolean>(false);
  const [timeValue, setTimeValue] = useState({});
  const [quickValue, setQuickValue] = useState({
    tab: 'common',
  });
  const { data: seasonInfo } = getLocaleState<TDatalabDateEvent>(DATALAB_DATE_EVENT) || {};
  /**
 * 处理初始化函数，根据传入的值设置时间相关的状态
 * @param {Object} value - 初始化的值对象，包含日期范围、日期类型和快速选择值等
 */
  const handleInit = (value = props?.value)=> {
    // 从传入的值中解构出日期范围、日期类型和快速选择值，并设置默认值
    const { dateRange=[], dateType, quickValue } = value;
    // 确定新的日期类型，如果原始类型是'day'或'hour'，则转换为'date'
    const newType = ['day', 'hour'].includes(dateType) ? 'date' : dateType;
    /**
     * 以下逻辑为解决两种场景:
     * 1. 当 Hour面板展示的是 Time选项时, 此时切换到其他日期类型时, 面板展示错误
     * 2. 当动态日期是month类型, 切换到静态日期, 应该转换为默认的Day类型
     */
    if (rangePickerRef.current?.currentType[0] !== newType) {
      rangePickerRef.current?.changeType([newType, newType]);
    }
    // 根据日期类型处理日期值
    // 如果是'hour'类型，则直接使用传入的日期范围
    // 否则，对日期范围的开始和结束应用相应的日期类型处理
    const newDateValue = dateType === 'hour' ?
      [moment(dateRange?.[0]), moment(dateRange?.[1])] :
      [moment(dateRange?.[0])?.startOf(dateType), moment(dateRange?.[1])?.endOf(dateType)];
    // 设置快速选择值
    setQuickValue(quickValue);
    // 设置时间相关的状态值，包括类型、日期值、是否显示时间、原始日期范围、日期类型和比较时间
    setTimeValue({
      type: [newType, newType],       // 日期类型数组
      newDateValue,                   // 处理后的日期值数组
      showTime: dateType === 'hour',  // 是否显示时间选择器
      dateRange,                      // 原始日期范围
      dateType,                       // 日期类型
      compareTimes: value?.compareTimes || []  // 比较时间，如果未提供则为空数组
    });
  };

  useEffect(() => {
    handleInit(value);
  }, [value]);
  const handleUserAction = useCallback(() => {
    const {overlayEle, labelEle, ...rest} = getOverlayPositionConfig?.() || {};
    if (!overlayEle || !labelEle) return;

    // 增加对比日期后手动调整位置
    adjustOverlayPositionByPlacement(overlayEle, {
      referenceElement: labelEle,
      offset: { mainAxis: 8, crossAxis: -11 },
      ...rest
    });
  }, []);
  
  // 拖拉拽页面是 close关闭面板;  报表编辑页面是 setPopActive关闭面板
  const newType = timeValue?.dateType === 'day' ? 'date' : timeValue?.dateType;
  const commonDataList = useMemo(()=> {
    const commonDataListObj: any = getDatalabShortcut(START_OF_DATE, {});
    const commonDataList = commonDataListObj?.[newType];
    return commonDataList;
  },[newType]);

  return (
    <ConfigProvider locale={isEn ? "en" : "cn"}>
      <RangePicker
        ref={rangePickerRef}
        className={classNames('datalab-static-date', className,
          {
            'datalab-static-date-hour': timeValue?.dateType === 'hour',
            'datalab-static-date-month': timeValue?.dateType === 'month',
          }
        )}
        header={(<>
          <DateTypeSegment isEn={isEn} value={timeValue.dateType} onChange={(newDateType: string) => {
            
            let newDateRange = [];
            if (newDateType === 'hour') {
              newDateRange = [
                moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
              ];
            } else {
              newDateRange = getLastDateRange(newDateType === 'day' ? 7 : 1, newDateType);
            }
            const newType = ['day', 'hour'].includes(newDateType) ? 'date' : newDateType;
            /**
             * 以下逻辑为解决两种场景:
             * 1. 当 Hour面板展示的是 Time选项时, 此时切换到其他日期类型时, 面板展示错误
             * 2. 当动态日期是month类型, 切换到静态日期, 应该转换为默认的Day类型
             */
            if (rangePickerRef.current?.currentType[0] !== newType) {
              rangePickerRef.current?.changeType([newType, newType]);
            }
            setQuickValue({
              tab: quickValue?.tab || 'common'
            });
            setTimeValue((timeValue) => ({
              ...timeValue,
              compareTimes: [],
              newDateValue: newDateType === 'hour' ?
                [moment(newDateRange?.[0]), moment(newDateRange?.[1])] :
                [moment(newDateRange?.[0])?.startOf(newDateType), moment(newDateRange?.[1])?.endOf(newDateType)],
              dateRange: newDateRange,
              dateType: newDateType,
              type: [newType, newType],
              showTime: newDateType === 'hour'
            }));
          }}/>
          <DateEventShortcut
            startOfTime={START_OF_DATE}
            latestDate={{}}
            commonDataList={commonDataList}
            type={newType}
            seasonInfo={seasonInfo}
            quickValue={quickValue}
            changeQuickValue={setQuickValue}
            handleChangeValue={(value: any, others: any) => {
              const {type, tab} = others || {};
              const dateRange = value?.map((item: moment.MomentInput) => moment(item).format(type === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'));
              // 选择事件切换到日粒度面板
              if (tab === 'event') {
                rangePickerRef.current?.changeType(['date', 'date']);
              }
              // 选择事件切换为日粒度
              setTimeValue((timeValue) => ({
                ...timeValue,
                ...(tab === 'event' ? {
                  dateType: 'day',
                  type: ['date', 'date'],
                  showTime: false,
                } : {}),
                compareTimes: tab === 'event' && type === 'date' ? timeValue?.compareTimes : [],
                newDateValue: [moment(dateRange?.[0]), moment(dateRange?.[1])],
                dateRange,
              }));
            }}
          />
        </>)}
        // type={timeValue?.type || []}
        calendarType={timeValue?.type?.[0]}
        value={timeValue?.newDateValue || []}
        startOfWeek={1}
        showTime={timeValue?.showTime}
        onChange={(newValue) => {
          let dateRange: string[] = [];
          if (timeValue.dateType === 'hour') {
            dateRange = [
              newValue[0]?.format('YYYY-MM-DD HH:mm:ss'),
              newValue[1]?.format('YYYY-MM-DD HH:mm:ss'),
            ];
          } else {
            dateRange = [
              newValue[0]?.startOf(timeValue.dateType)?.format('YYYY-MM-DD'),
              newValue[1]?.endOf(timeValue.dateType)?.format('YYYY-MM-DD'),
            ];
          }
          setTimeValue((timeValue) => ({
            ...timeValue,
            // type: newValue,
            newDateValue: newValue,
            dateRange,
          }));
          setQuickValue({
            tab: quickValue?.tab || 'common'
          });
          // onChange({dateRange: , dateType: value.dateType}, true);
        }}
        overlayClassName={'dbl_multi_date dbl-static-date'}
        label={children}
        // range={[]} // 日期选择范围
        {...reset}
        changeClose={false}
        onOpenChange={(open: boolean) => {
          if (open) {
            // 重置confirm状态
            isConfirmRef.current = false;
          } else {
            // 把静态日期面板关闭 rangePickerRef.current?.setPopActive()
            // 和 上一级面板关闭 close 分开处理
            // 可以优化两个面板关闭过渡效果. 避免上一级面板关闭了, 静态日期面板还展示的问题.
            // 并且是 点击了confirm按钮导致的关闭 , 不是其他方式
            isConfirmRef.current && close?.();
          }
          // 关闭弹窗, 且未点击确认按钮, 将日期恢复成原来的日期
          if (!open && (!isConfirmRef.current)) {
            handleInit();
          }
        }}
        footerConfig={{
          left: (<CompareSwitch
            value={timeValue?.compareTimes?.length > 0}
            onChange={(open: boolean) => {
              if (open) {
                const compare = getPreviousPeriodValue(timeValue?.dateRange, timeValue?.dateType);
                setTimeValue((timeValue) => ({...timeValue, compareTimes: [compare]}));
                
                // 在timeValue状态更新, 新面板展示后, 再计算新位置, 保证计算位置正确
                setTimeout(() => {
                  handleUserAction();
                }, 0);
              }
            }}
            onConfirm={() => {
              isConfirmRef.current = true;
              rangePickerRef.current?.setPopActive();
              onChange({dateRange: timeValue.dateRange, dateType: timeValue.dateType, compareTimes: [], quickValue});
            }}
            onCancel={() => rangePickerRef.current?.setPopActive()}
            disabledSwitch={isDisabledCompare}
          />)
        }}
        overlay={
          (!isDisabledCompare && timeValue?.compareTimes?.length > 0) ?
            (<BaseTimeAndCompareRange
              value={timeValue}
              onConfirm={(newValue) => {
                isConfirmRef.current = true;
                rangePickerRef.current?.setPopActive();
                setTimeout(() => {
                  onChange(newValue);
                }, 300);
              }}
              onCancel={() =>{ rangePickerRef.current?.setPopActive() }}
              mountClassNames={'.dbl_multi_date.dbl-static-date'}
              updatePosition={handleUserAction}
            />) : undefined
        }
      />
    </ConfigProvider>
  );
}

export default StaticDate;